import React, { useState, useEffect } from "react";
import { apiFetch } from "../../services/api";
import { createDevConsole } from "../../utils/devLogger";

const console = createDevConsole();

const ReportFilters = ({ filters, onFiltersChange }) => {
  const [owners, setOwners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    setLoading(true);
    try {
      // Load all reports to extract unique owners and categories
      const response = await apiFetch("/api/reports");
      if (response.ok) {
        const data = await response.json();
        const reports = data.reports || [];

        // Extract unique owners with names
        const ownerMap = new Map();
        reports.forEach((r) => {
          if (r.created_by) {
            const ownerName = r.created_by_name || r.created_by;
            ownerMap.set(r.created_by, ownerName);
          }
        });
        // Convert to array of {id, name} objects for display
        const ownersList = Array.from(ownerMap.entries()).map(([id, name]) => ({
          id,
          name,
        }));
        setOwners(ownersList);

        // Extract unique categories
        const uniqueCategories = [
          ...new Set(reports.map((r) => r.category).filter(Boolean)),
        ];
        setCategories(uniqueCategories);
      }
    } catch (err) {
      console.error("Error loading filter options:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      owner: "",
      reportType: "",
      category: "",
      isFavorite: null,
    });
  };

  const hasActiveFilters =
    filters.search ||
    filters.owner ||
    filters.reportType ||
    filters.category ||
    filters.isFavorite !== null;

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Search & Filter</h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Search
          </label>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            placeholder="Search by name..."
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Owner */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Owner
          </label>
          <select
            value={filters.owner}
            onChange={(e) => handleFilterChange("owner", e.target.value)}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All Owners</option>
            {owners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </select>
        </div>

        {/* Report Type */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Report Type
          </label>
          <select
            value={filters.reportType}
            onChange={(e) => handleFilterChange("reportType", e.target.value)}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All Types</option>
            <option value="roadmap">Roadmap</option>
            <option value="data-quality">Data Quality</option>
            <option value="relationship">Relationship Analysis</option>
          </select>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Category
          </label>
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange("category", e.target.value)}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        {/* Favorite */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Favorite
          </label>
          <select
            value={
              filters.isFavorite === null ? "" : filters.isFavorite.toString()
            }
            onChange={(e) =>
              handleFilterChange(
                "isFavorite",
                e.target.value === "" ? null : e.target.value === "true"
              )
            }
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All</option>
            <option value="true">Favorites Only</option>
            <option value="false">Non-Favorites</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default ReportFilters;
